'use client';
import React, { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Plus, Clock, UserMinus, X, Check, Info } from 'lucide-react';
import AppLayout from '@/components/AppLayout';
import AppImage from '@/components/ui/AppImage';
import {
  type FamilyContact,
} from '@/lib/familyDemoData';
import { useFamilyDemo } from '@/lib/familyDemoState';

// Type aliases for backward compat
type ContactPermissions = FamilyContact['permissions'];
type ContactStatus = FamilyContact['status'];

// ─── Permission labels ────────────────────────────────────────────────────────

const PERMISSION_LABELS: { key: keyof ContactPermissions; label: string }[] = [
  { key: 'canCall', label: 'Appeler André' },
  { key: 'canReceiveCallbacks', label: 'Recevoir les demandes de rappel' },
  { key: 'canSendMessages', label: 'Envoyer des messages' },
  { key: 'canSendPhotos', label: 'Envoyer des photos' },
  { key: 'canEditAgenda', label: "Ajouter des événements à l'agenda" },
  { key: 'canReceiveConnectionAlerts', label: 'Recevoir les alertes de connexion' },
  { key: 'canViewActivities', label: 'Consulter les activités récentes' },
  { key: 'canEditSettings', label: "Modifier les paramètres d'André" },
];

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ContactStatus }) {
  const map: Record<ContactStatus, { label: string; cls: string }> = {
    admin: 'bg-lucat-navy text-white',
    authorized: 'bg-lucat-green-light text-lucat-green',
    secondary: 'bg-gray-100 text-gray-600',
    pending_invite: 'bg-orange-100 text-orange-700',
  } as unknown as Record<ContactStatus, string>;

  const labels: Record<ContactStatus, string> = {
    admin: 'Administratrice',
    authorized: 'Proche autorisé',
    secondary: 'Contact secondaire',
    pending_invite: 'Invitation en attente',
  };

  const cls: Record<ContactStatus, string> = {
    admin: 'bg-lucat-navy text-white',
    authorized: 'bg-lucat-green-light text-lucat-green',
    secondary: 'bg-gray-100 text-gray-600',
    pending_invite: 'bg-orange-100 text-orange-700',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cls[status]}`}>
      {labels[status]}
    </span>
  );
}

// ─── Toggle switch ────────────────────────────────────────────────────────────

interface ToggleProps {
  checked: boolean;
  onChange: (val: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
}

function Toggle({ checked, onChange, ariaLabel, disabled = false }: ToggleProps) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lucat-green ${
        disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
      }`}
      style={{ width: 48, height: 28, flexShrink: 0 }}
    >
      <span
        className={`absolute inset-0 rounded-full transition-colors ${checked ? 'bg-lucat-green' : 'bg-gray-200'}`}
      />
      <span
        className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0'}`}
      />
    </button>
  );
}

// ─── Edit / View modal ────────────────────────────────────────────────────────

interface EditModalProps {
  contact: FamilyContact;
  onClose: () => void;
  onSave: (updated: FamilyContact) => void;
  onRemove: (id: string) => void;
}

function EditModal({ contact, onClose, onSave, onRemove }: EditModalProps) {
  const [draft, setDraft] = useState<FamilyContact>({ ...contact, permissions: { ...contact.permissions } });
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const togglePerm = (key: keyof ContactPermissions) => {
    setDraft((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: !prev.permissions[key] },
    }));
  };

  if (showRemoveConfirm) {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" role="dialog" aria-modal="true" aria-label={`Retirer ${contact.firstName}`}>
        <div className="w-full max-w-md bg-white rounded-t-3xl p-6 pb-8 shadow-call">
          <h2 className="text-lg font-bold text-lucat-navy mb-2">Retirer ce proche ?</h2>
          <p className="text-sm text-gray-600 mb-6">
            Cette personne ne pourra plus contacter André depuis LUCAT CONNECT.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => onRemove(contact.id)}
              className="w-full bg-red-600 text-white font-semibold rounded-xl py-3 text-sm"
              style={{ minHeight: 48 }}
              aria-label={`Confirmer le retrait de ${contact.firstName}`}
            >
              Retirer le proche
            </button>
            <button
              onClick={() => setShowRemoveConfirm(false)}
              className="w-full bg-gray-100 text-lucat-navy font-semibold rounded-xl py-3 text-sm"
              style={{ minHeight: 48 }}
              aria-label="Annuler le retrait"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" role="dialog" aria-modal="true" aria-label={`Modifier ${contact.firstName}`}>
      <div className="w-full max-w-md bg-white rounded-t-3xl shadow-call flex flex-col" style={{ maxHeight: '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <h2 className="text-lg font-bold text-lucat-navy">Modifier le contact</h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            style={{ width: 36, height: 36 }}
            aria-label="Fermer"
          >
            <X size={18} className="text-gray-600" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Identity */}
          <div className="flex items-center gap-4">
            <div className="rounded-full overflow-hidden border-2 border-lucat-green flex-shrink-0" style={{ width: 64, height: 64 }}>
              <AppImage
                src={draft.avatarUrl}
                alt={`Portrait de ${draft.firstName} ${draft.lastName}`}
                width={64}
                height={64}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="font-bold text-lucat-navy text-base">{draft.firstName} {draft.lastName}</p>
              <p className="text-sm text-gray-500">{draft.relationship}</p>
              <p className="text-sm text-gray-500">{draft.phone}</p>
              <div className="mt-1"><StatusBadge status={draft.status} /></div>
            </div>
          </div>

          {/* Permissions */}
          <div>
            <h3 className="text-sm font-bold text-lucat-navy mb-3">Autorisations</h3>
            <div className="space-y-1">
              {PERMISSION_LABELS.map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                  <span className="text-sm text-gray-700 pr-3 flex-1">{label}</span>
                  <Toggle
                    checked={draft.permissions[key]}
                    onChange={(val) => togglePerm(key)}
                    ariaLabel={`Autoriser ${draft.firstName} à — ${label}`}
                    disabled={draft.isAdmin}
                  />
                </div>
              ))}
            </div>
            {draft.isAdmin && (
              <p className="text-xs text-gray-400 mt-2 italic">L'administratrice dispose de tous les droits.</p>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 border-t border-gray-100 space-y-3">
          <button
            onClick={() => onSave(draft)}
            className="w-full bg-lucat-green text-white font-semibold rounded-xl py-3 text-sm flex items-center justify-center gap-2"
            style={{ minHeight: 48 }}
            aria-label="Enregistrer les modifications"
          >
            <Check size={16} />
            Enregistrer les modifications
          </button>
          {!contact.isAdmin && (
            <button
              onClick={() => setShowRemoveConfirm(true)}
              className="w-full bg-red-50 text-red-600 font-semibold rounded-xl py-3 text-sm flex items-center justify-center gap-2"
              style={{ minHeight: 48 }}
              aria-label={`Retirer ${contact.firstName} du cercle familial`}
            >
              <UserMinus size={16} />
              Retirer ce proche
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-lucat-navy font-semibold rounded-xl py-3 text-sm"
            style={{ minHeight: 48 }}
            aria-label="Annuler les modifications"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add contact modal ────────────────────────────────────────────────────────

interface AddModalProps {
  onClose: () => void;
  onAdd: (contact: FamilyContact) => void;
}

const DEFAULT_PERMS: ContactPermissions = {
  canCall: true,
  canReceiveCallbacks: false,
  canSendMessages: true,
  canSendPhotos: false,
  canEditAgenda: false,
  canReceiveConnectionAlerts: false,
  canViewActivities: false,
  canEditSettings: false,
};

function AddModal({ onClose, onAdd }: AddModalProps) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    relationship: '',
    phone: '',
    email: '',
  });
  const [perms, setPerms] = useState<ContactPermissions>({ ...DEFAULT_PERMS });
  const [sent, setSent] = useState(false);

  const togglePerm = (key: keyof ContactPermissions) => {
    setPerms((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSend = () => {
    if (!form.firstName.trim()) return;
    const newContact: FamilyContact = {
      id: `fc-${Date.now()}`,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      relationship: form.relationship.trim() || 'Proche',
      phone: form.phone.trim(),
      email: form.email.trim(),
      avatarUrl: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face',
      status: 'pending_invite',
      isActive: false,
      isAdmin: false,
      permissions: perms,
    };
    onAdd(newContact);
    setSent(true);
  };

  if (sent) {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" role="dialog" aria-modal="true" aria-label="Invitation envoyée">
        <div className="w-full max-w-md bg-white rounded-t-3xl p-8 shadow-call text-center">
          <div className="w-16 h-16 bg-lucat-green-light rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={32} className="text-lucat-green" />
          </div>
          <h2 className="text-xl font-bold text-lucat-navy mb-2">Invitation envoyée</h2>
          <p className="text-sm text-gray-600 mb-6">
            Cette personne recevra les instructions pour rejoindre le cercle familial d'André.
          </p>
          <button
            onClick={onClose}
            className="w-full bg-lucat-green text-white font-semibold rounded-xl py-3 text-sm"
            style={{ minHeight: 48 }}
            aria-label="Fermer la confirmation"
          >
            Fermer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" role="dialog" aria-modal="true" aria-label="Ajouter un proche">
      <div className="w-full max-w-md bg-white rounded-t-3xl shadow-call flex flex-col" style={{ maxHeight: '92vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <h2 className="text-lg font-bold text-lucat-navy">Ajouter un proche</h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            style={{ width: 36, height: 36 }}
            aria-label="Fermer"
          >
            <X size={18} className="text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Form fields */}
          <div className="space-y-3">
            {[
              { id: 'firstName', label: 'Prénom *', placeholder: 'Prénom', value: form.firstName },
              { id: 'lastName', label: 'Nom', placeholder: 'Nom de famille', value: form.lastName },
              { id: 'relationship', label: 'Lien avec André', placeholder: 'Ex : Neveu, Amie…', value: form.relationship },
              { id: 'phone', label: 'Numéro de téléphone', placeholder: '06 00 00 00 00', value: form.phone },
              { id: 'email', label: 'Adresse e-mail', placeholder: 'email@exemple.fr', value: form.email },
            ].map(({ id, label, placeholder, value }) => (
              <div key={id}>
                <label htmlFor={`add-${id}`} className="block text-xs font-semibold text-lucat-navy mb-1">{label}</label>
                <input
                  id={`add-${id}`}
                  type={id === 'email' ? 'email' : id === 'phone' ? 'tel' : 'text'}
                  placeholder={placeholder}
                  value={value}
                  onChange={(e) => setForm((prev) => ({ ...prev, [id]: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-lucat-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lucat-green"
                  style={{ minHeight: 48 }}
                />
              </div>
            ))}
          </div>

          {/* Initial permissions */}
          <div>
            <h3 className="text-sm font-bold text-lucat-navy mb-3">Autorisations initiales</h3>
            <div className="space-y-1">
              {PERMISSION_LABELS.map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                  <span className="text-sm text-gray-700 pr-3 flex-1">{label}</span>
                  <Toggle
                    checked={perms[key]}
                    onChange={(val) => togglePerm(key)}
                    ariaLabel={`Autorisation initiale — ${label}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 space-y-3">
          <button
            onClick={handleSend}
            disabled={!form.firstName.trim()}
            className={`w-full font-semibold rounded-xl py-3 text-sm transition-colors ${
              form.firstName.trim()
                ? 'bg-lucat-green text-white' :'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            style={{ minHeight: 48 }}
            aria-label="Envoyer une invitation"
          >
            Envoyer une invitation
          </button>
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-lucat-navy font-semibold rounded-xl py-3 text-sm"
            style={{ minHeight: 48 }}
            aria-label="Annuler l'ajout d'un proche"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Contact card ─────────────────────────────────────────────────────────────

interface ContactCardProps {
  contact: FamilyContact;
  onEdit: (contact: FamilyContact) => void;
}

function ContactCard({ contact, onEdit }: ContactCardProps) {
  return (
    <div className="bg-white rounded-2xl shadow-card p-4 flex flex-col gap-3">
      {/* Top row */}
      <div className="flex items-center gap-3">
        <div className="relative flex-shrink-0">
          <div className="rounded-full overflow-hidden border-2 border-lucat-green" style={{ width: 56, height: 56 }}>
            <AppImage
              src={contact.avatarUrl}
              alt={`Portrait de ${contact.firstName} ${contact.lastName}`}
              width={56}
              height={56}
              className="w-full h-full object-cover"
            />
          </div>
          {/* Active indicator */}
          <span
            className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white ${contact.isActive ? 'bg-lucat-green' : 'bg-gray-300'}`}
            aria-label={contact.isActive ? 'Actif' : 'Inactif'}
          />
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-bold text-lucat-navy text-base leading-tight">{contact.firstName}</p>
          <p className="text-sm text-gray-500">{contact.relationship}</p>
          <div className="mt-1 flex flex-wrap gap-1 items-center">
            <StatusBadge status={contact.status} />
            <span className={`text-xs font-medium ${contact.isActive ? 'text-lucat-green' : 'text-gray-400'}`}>
              {contact.isActive ? '● Actif' : '○ Inactif'}
            </span>
          </div>
        </div>

        <button
          onClick={() => onEdit(contact)}
          className="flex-shrink-0 bg-lucat-navy text-white text-xs font-semibold rounded-xl px-4 py-2 hover:bg-lucat-navy/90 transition-colors"
          style={{ minHeight: 44, minWidth: 80 }}
          aria-label={`Modifier les autorisations de ${contact.firstName}`}
        >
          Modifier
        </button>
      </div>

      {/* Permissions summary */}
      {contact.status !== 'pending_invite' && (
        <div className="flex flex-wrap gap-1.5 pt-1 border-t border-gray-50">
          {PERMISSION_LABELS.filter(({ key }) => contact.permissions[key]).map(({ key, label }) => (
            <span key={key} className="inline-flex items-center gap-1 bg-lucat-green-light text-lucat-green text-xs px-2 py-0.5 rounded-full">
              <Check size={10} />
              {label}
            </span>
          ))}
          {PERMISSION_LABELS.filter(({ key }) => contact.permissions[key]).length === 0 && (
            <span className="text-xs text-gray-400 italic">Aucune autorisation active</span>
          )}
        </div>
      )}

      {contact.status === 'pending_invite' && (
        <div className="flex items-center gap-2 pt-1 border-t border-gray-50">
          <Clock size={14} className="text-orange-500 flex-shrink-0" />
          <span className="text-xs text-orange-600">En attente d'acceptation de l'invitation</span>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ContactsClient() {
  const router = useRouter();
  const { state, updateFamilyContact, removeFamilyContact, addFamilyContact } = useFamilyDemo();
  const contacts = state.familyContacts;
  const [editingContact, setEditingContact] = useState<FamilyContact | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const handleSave = useCallback((updated: FamilyContact) => {
    updateFamilyContact(updated.id, updated);
    setEditingContact(null);
  }, [updateFamilyContact]);

  const handleRemove = useCallback((id: string) => {
    removeFamilyContact(id);
    setEditingContact(null);
  }, [removeFamilyContact]);

  const handleAdd = useCallback((contact: FamilyContact) => {
    addFamilyContact(contact);
  }, [addFamilyContact]);

  const activeCount = contacts.filter((c) => c.status !== 'pending_invite').length;

  return (
    <AppLayout>
      {/* Sub-header */}
      <div className="bg-lucat-navy px-4 pt-4 pb-5">
        {/* Back button */}
        <button
          onClick={() => router.back()}
          className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium mb-4 transition-colors"
          style={{ minHeight: 44 }}
          aria-label="Retour à la page précédente"
        >
          <ArrowLeft size={18} />
          Retour
        </button>

        <h1 className="text-xl font-bold text-white leading-tight">Contacts et autorisations</h1>
        <p className="text-sm text-white/70 mt-0.5">Les personnes qui accompagnent André</p>

        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-white/80 font-medium">
            {activeCount} proche{activeCount > 1 ? 's' : ''} autorisé{activeCount > 1 ? 's' : ''}
          </span>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 bg-lucat-green text-white text-sm font-semibold rounded-xl px-4 py-2.5 hover:bg-lucat-green/90 transition-colors"
            style={{ minHeight: 44 }}
            aria-label="Ajouter un proche au cercle familial"
          >
            <Plus size={16} />
            Ajouter un proche
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-5 space-y-4 pb-6">
        {/* Info banner */}
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3">
          <Info size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800 leading-relaxed">
              Seules les personnes autorisées peuvent consulter les informations d'André ou le contacter depuis LUCAT CONNECT.
            </p>
            <button
              className="text-xs text-blue-600 font-semibold mt-1.5 underline underline-offset-2"
              aria-label="En savoir plus sur les autorisations"
            >
              En savoir plus sur les autorisations
            </button>
          </div>
        </div>

        {/* Contact list */}
        <div className="space-y-3">
          {contacts.map((contact) => (
            <ContactCard
              key={contact.id}
              contact={contact}
              onEdit={setEditingContact}
            />
          ))}
        </div>
      </div>

      {/* Edit modal */}
      {editingContact && (
        <EditModal
          contact={editingContact}
          onClose={() => setEditingContact(null)}
          onSave={handleSave}
          onRemove={handleRemove}
        />
      )}

      {/* Add modal */}
      {showAddModal && (
        <AddModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAdd}
        />
      )}
    </AppLayout>
  );
}
